import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Textarea, TextareaProps } from '@rebass/forms';
import { Flex } from 'rebass';
import { Label } from '../Form';
import { UtilityIconButton } from '../Button/UtilityIconButton';
import PropTypes from 'prop-types';
import Button from '../Button/Button';

const RichText: React.FC<
  TextareaProps & { label: string; variableOptions?: string[] }
> = (props) => {
  const textArea = useRef<HTMLInputElement>(null);

  const [selectionStart, setSelectionStart] = useState<number>(0);

  const [selectionEnd, setSelectionEnd] = useState<number>(0);

  // When the component mounts, set cursor to end of message.
  useEffect(() => {
    const message = textArea.current?.value;
    if (message) {
      textArea.current.selectionStart = message.length;
    }
  }, []);

  const setValue = useCallback(
    (value: string) => {
      if (!textArea.current) {
        return;
      }
      const input = textArea.current;
      const previousValue = input.value;

      input.value = value;

      const tracker = (input as any)._valueTracker;
      if (tracker) {
        tracker.setValue(previousValue);
      }

      // 'change' instead of 'input', see https://github.com/facebook/react/issues/11488#issuecomment-381590324
      input.dispatchEvent(new Event('change', { bubbles: true }));
    },
    [textArea.current]
  );

  const handleClick = useCallback(
    (openTag: string, closeTag: string) => {
      if (selectionStart && selectionEnd) {
        const openTagLength = openTag.length;
        const selectedText = textArea.current?.value.slice(
          selectionStart,
          selectionEnd
        );
        const messageEnd = textArea.current?.value.slice(selectionEnd);
        if (
          selectedText?.slice(0, openTagLength) === openTag &&
          selectedText?.slice(-(openTagLength + 1)) === closeTag
        ) {
          setValue(
            `${textArea.current?.value.slice(
              0,
              selectionStart
            )}${selectedText?.slice(
              openTagLength,
              -(openTagLength + 1)
            )}${messageEnd}`
          );
        } else {
          setValue(
            `${textArea.current?.value.slice(
              0,
              selectionStart
            )}${openTag}${selectedText}${closeTag}${messageEnd}`
          );
        }
      }
    },
    [selectionStart, selectionEnd, textArea]
  );

  const addTemplateOption = (option: React.MouseEvent<HTMLElement>) => {
    const selectedOption = (option.target as any).value;
    if (selectionStart !== null && selectionEnd !== null) {
      const messageStart = `${textArea.current?.value.slice(
        0,
        selectionStart
      )}[${selectedOption}]`;
      const messageEnd = textArea.current?.value.slice(selectionEnd);
      setSelectionStart(messageStart.length);
      setSelectionEnd(messageStart.length);
      setValue(`${messageStart}${messageEnd}`);
    }
  };

  const hasSelection = selectionStart !== null && !!selectionEnd;

  const buttons = useMemo(() => {
    return (
      <>
        <UtilityIconButton
          dark
          icon="bold"
          disabled={!hasSelection}
          sx={{
            borderTopRightRadius: 'default',
            borderBottomRightRadius: 'default',
          }}
          onClick={() => handleClick('[b]', '[/b]')}
        />
        <UtilityIconButton
          dark
          icon="italic"
          disabled={!hasSelection}
          sx={{
            borderRadius: 'none',
            mx: '1px',
          }}
          onClick={() => handleClick('[i]', '[/i]')}
        />

        <UtilityIconButton
          dark
          icon="underline"
          disabled={!hasSelection}
          sx={{
            borderTopLeftRadius: 'default',
            borderBottomLeftRadius: 'default',
          }}
          onClick={() => handleClick('[u]', '[/u]')}
        />
        <UtilityIconButton
          dark
          icon="external-link"
          disabled={!hasSelection}
          ml={2}
          onClick={() => handleClick('[link]', '[/link]')}
        />

        <UtilityIconButton
          dark
          icon="text-center"
          disabled={!hasSelection}
          ml={2}
          onClick={() => handleClick('[center]', '[/center]')}
        />
      </>
    );
  }, [handleClick, hasSelection]);

  return (
    <>
      <Flex mb={1}>
        <Label htmlFor={props.label}>{props.label}</Label>
        {buttons}
      </Flex>
      <Textarea
        ref={textArea}
        sx={{ flexGrow: 1, width: 'auto', minHeight: '125px' }}
        mb={3}
        variant={props.variant || 'textarea'}
        {...(props as any)}
        onSelect={(e) => {
          setSelectionStart(textArea.current?.selectionStart || 0);
          setSelectionEnd(textArea.current?.selectionEnd || 0);
          if (props.onSelect) {
            props.onSelect(e);
          }
        }}
      />
      <Flex flexWrap="wrap">
        {props.variableOptions?.map((option: string) => (
          <Flex
            key={option}
            mr={1}
            mb={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              value={option}
              variant="outline"
              onClick={addTemplateOption}
              sx={{
                fontSize: '11px',
              }}
            >
              {option}
            </Button>
          </Flex>
        ))}
      </Flex>
    </>
  );
};

RichText.propTypes = {
  value: PropTypes.string,
  variant: PropTypes.string,
  onSelect: PropTypes.func,
  label: PropTypes.any,
};

export default RichText;
